import React, { useEffect, useState } from 'react';
import { GetOneResult, Identifier, ReduxState, useDataProvider, useTranslate } from 'react-admin';
import DashboardBox from '../../commons/dashboard-box/dashboard-box';
import DashboardTable from '../../commons/dashboard-table/dashboard-table';
import GetMetabaseUrlOfTheData from '../../commons/get-metabase-url-of-the-data/get-metabase-url-of-the-data';
import { ResponseForwarderToMetabaseType } from '../../utils/ticketingBridgeMetabase/viewsDynamic';
import { Avatar, ChartDashboardComponent, FieldsDashboard } from '../../utils/types';
import TableChartIcon from '@material-ui/icons/TableChart';
import { useDispatch, useSelector } from 'react-redux';
import { CustomReducerState } from '../../redux/reducers';
import extractIdFromURI from '../../utils/id';
import Tooltip from '@material-ui/core/Tooltip';
import ShowTreeDialog from '../../commons/dialog/ShowTreeDialog';
import { AVATARS } from '../../providers/resources';
import { updateGlobalDashboard } from '../../redux/actions/dashboard';
import { MenuItemType } from '../../commons/menuButtonActions';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import { Typography } from '@material-ui/core';
import OptionMenu from '../statistics/actions/OptionMenu';
import makeStyles from '@material-ui/styles/makeStyles';
import { addDays } from '../../utils/date';

const useStyles = makeStyles(() => ({
    boldUppercase: {
        padding: 0,
        textTransform: 'uppercase',
        fontSize: 13,
        fontWeight: 'bold',
    },
}));

const RenderData = ({ data }: { data: ResponseForwarderToMetabaseType }) => {
    const translate = useTranslate();

    const fieldsToShow: FieldsDashboard = {
        ranking: { label: translate('app.dashboards.topIncidentsDashboard.fields.ranking') },
        metadata_avatar_type: { label: translate('app.dashboards.topIncidentsDashboard.fields.incidentType') },
        event_amount: { label: translate('app.dashboards.topIncidentsDashboard.fields.numberOfIncidents') },
        event_percent: {
            label: translate('app.dashboards.topIncidentsDashboard.fields.incidentPercent'),
            type: 'percent',
        },
    };

    return <DashboardTable data={data} fieldsToShow={fieldsToShow} />;
};

interface Props extends ChartDashboardComponent {
    dialogTitle: string;
    formFieldName: string;
    label: string;
    dashboardTitle: string;
    dialogHelpText: string;
    infoText: string;
    requestQuestionId: number;
}

const PLACE = 'place';
const EVENT_RUNNING = 'event_running';

interface ConfigProp {
    [EVENT_RUNNING]?: boolean | null;
    [PLACE]?: Identifier | null;
}

const TopIncidentsDashboard = (props: Props) => {
    const translate = useTranslate();
    const classes = useStyles();
    const dispatch = useDispatch();
    const dataProvider = useDataProvider();

    const [version, setVersion] = useState(0);
    const [showTreeDialog, setShowTreeDialog] = useState<boolean>(false);

    const periodConfigs = useSelector((state: CustomReducerState) => state.ticketing.dashboards.globalFilters.period);
    const dashboardVersion: number = useSelector((state: CustomReducerState) => state.ticketing.dashboards.version);

    const config: ConfigProp | undefined = useSelector((state: CustomReducerState) => {
        const ownDashboardType = state.ticketing.dashboards.global[props.id];
        const chartRegisterData = ownDashboardType.find((item) => item.instanceId === props.instanceId);
        return chartRegisterData ? chartRegisterData.customFields : undefined;
    });

    const isEventRunningEnable: boolean | undefined =
        config && EVENT_RUNNING in config ? (config[EVENT_RUNNING] as boolean) : undefined;

    const [previousEventRunningEnable, setPreviousEventRunningEnable] = useState<boolean | undefined>(false);

    const placeIdSelected: string | undefined = config && PLACE in config ? (config[PLACE] as string) : undefined;
    const [placeSelected, setPlaceSelected] = useState<Avatar | undefined>(undefined);

    useEffect(() => {
        updateConfig({ [EVENT_RUNNING]: true });
        setPreviousEventRunningEnable(true);
    }, []);

    // update version when global dashboard versio change
    useEffect(() => {
        if (dashboardVersion) {
            setVersion(version + 1);
        }
    }, [dashboardVersion]);

    // get place selected and update version
    useEffect(() => {
        if (placeSelected) {
            setVersion(version + 1);
        }
    }, [placeSelected]);

    useEffect(() => {
        if (placeIdSelected) {
            // search place
            dataProvider
                .getOne(
                    AVATARS,
                    { id: placeIdSelected },
                    {
                        onSuccess: (response: GetOneResult) => {
                            if (response && 'data' in response && response.data) {
                                // @ts-ignore
                                setPlaceSelected(response.data as Avatar);
                            }
                        },
                        onFailure: (error: any) => {
                            console.error('ERRROR getting one ', AVATARS, error);
                            setPlaceSelected(undefined);
                        },
                    }
                )
                .catch((e: any) => {
                    console.error('Catch exception getting one ', e);
                    setPlaceSelected(undefined);
                });
        }
    }, [placeIdSelected]);

    useEffect(() => {
        if (config && EVENT_RUNNING in config && config[EVENT_RUNNING] !== previousEventRunningEnable) {
            setVersion(version + 1);
        }
    }, [config]);

    const validateSelectedPlace = (place?: Avatar) => {
        if (!place) return;
        updateConfig({ [PLACE]: place.id });
    };

    const updateConfig = (config: ConfigProp) => {
        dispatch(updateGlobalDashboard.fn(props.id, props.instanceId, config));
    };

    useEffect(() => {
        if (periodConfigs && periodConfigs.enabled && periodConfigs.afterAt && periodConfigs.beforeAt) {
            setVersion(version + 1);
        }
        if (periodConfigs && !periodConfigs.enabled && !periodConfigs.afterAt && !periodConfigs.beforeAt) {
            setVersion(version + 1);
        }
    }, [periodConfigs]);

    const handleChangeEventRunning = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPreviousEventRunningEnable(isEventRunningEnable);
        updateConfig({ [EVENT_RUNNING]: event.target.checked });
    };

    const optionMenu: MenuItemType[] = [
        {
            icon: null,
            text: translate('app.dashboards.topIncidentsDashboard.eventRunning'),
            disabled: false,
            renderComponent: () => (
                <FormControlLabel
                    control={
                        <Checkbox
                            color={'primary'}
                            checked={isEventRunningEnable}
                            onChange={handleChangeEventRunning}
                            name='eventRunning'
                        />
                    }
                    label={
                        <Typography color={'primary'} variant={'body1'} className={classes.boldUppercase}>
                            {translate('app.dashboards.topIncidentsDashboard.eventRunning')}
                        </Typography>
                    }
                />
            ),
            roles: [],
        },
        {
            icon: null,
            onClick: () => setShowTreeDialog(true),
            text: translate('app.dashboards.topIncidentsDashboard.place'),
            disabled: false,
            roles: [],
        },
    ];

    return (
        <>
            <DashboardBox
                config={<OptionMenu buttonLabel={'app.dashboards.option'} menuActions={optionMenu} />}
                subtitle={
                    placeSelected
                        ? translate('app.dashboards.topIncidentsDashboard.in', {
                              name: (placeSelected as Avatar).name,
                          })
                        : translate('app.dashboards.topIncidentsDashboard.nonePlace')
                }
                icon={
                    <Tooltip title={translate(props.infoText, { question: props.requestQuestionId })}>
                        <TableChartIcon />
                    </Tooltip>
                }
                onClose={props.close}
                title={translate('app.dashboards.topIncidentsDashboard.title')}
            >
                <GetMetabaseUrlOfTheData
                    version={version}
                    requestBody={{
                        id: props.requestQuestionId,
                        dashboard_type: 'question',
                        opened_incidents: null,
                        place_id: placeSelected ? extractIdFromURI(placeSelected.id as string) : null,
                        creation_date_before: periodConfigs.beforeAt ? addDays(periodConfigs.beforeAt) : null,
                        creation_date_after: periodConfigs.afterAt ? periodConfigs.afterAt : null,
                        event_running: isEventRunningEnable ? 'true' : 'false',
                    }}
                >
                    {(data: any) => <RenderData data={data} />}
                </GetMetabaseUrlOfTheData>
            </DashboardBox>
            {showTreeDialog && (
                <ShowTreeDialog
                    closeAfterValidate={true}
                    dialogTitle={props.dialogTitle}
                    dialogHelpText={props.dialogHelpText}
                    handleValidate={validateSelectedPlace}
                    showButton={false}
                    onClose={() => setShowTreeDialog(false)}
                />
            )}
        </>
    );
};

export default TopIncidentsDashboard;
